/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Fragment, Ref } from 'preact';
import { useEffect, useState, useCallback } from 'preact/hooks';
import { forwardRef, ElementRef } from 'preact/compat';

import { BareButton } from '../UNSAFE_BareButton';
import { IconButton } from '../UNSAFE_IconButton';
import { useToggleAction, ToggleDetail } from '../hooks/UNSAFE_useToggleAction';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';
import { useHover } from '../hooks/UNSAFE_useHover';
import { useActive } from '../hooks/UNSAFE_useActive';
import { useColorScheme } from '../hooks/UNSAFE_useColorScheme';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { ColorSwatchVariantOptions } from './themes/ColorSwatchStyles.css';
import { ColorSwatchRedwoodTheme } from './themes/redwood/ColorSwatchTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { NoFill } from './NoFill';
import { processGlobals } from '../utils/UNSAFE_buttonUtils';
import type { ButtonIntrinsicProps, ButtonIntrinsicAriaProps } from '../utils/UNSAFE_buttonUtils';
import {
  parseColorValue,
  type ColorValue,
  isEightDigitHex,
  getSixDigitHex
} from '../utils/UNSAFE_color';
import translations from '#resources/nls/en/bundle';
import { CheckboxCrossed } from '#PRIVATE_Icons/CheckboxCrossed';
import { classNames } from '#utils/UNSAFE_classNames';

type SwatchHandle = ElementRef<typeof BareButton>;

export type ColorSwatchProps = Partial<ButtonIntrinsicProps> &
  Partial<ButtonIntrinsicAriaProps> &
  TestIdProps & {
    /**
     * color of the swatch
     */
    value?: ColorValue;

    /**
     * The size indicates how large the color swatch is rendered.
     */
    size?: 'xs' | 'sm';

    /**
     * Specifies if the color swatch is selected
     */
    isSelected?: boolean;

    /**
     * Specifies if the color swatch has role = button.  By default, the color swatch is used in a
     * palette whereby it is not a button, but just a color swatch inside a grid cell.  However,
     * it can be used as a stand-alone button, in which case this property should be set to true.  For
     * example, inside inputColor, the color swatch is a button.
     */
    isButton?: boolean;

    /**
     * Specifies if the color swatch is disabled
     */
    isDisabled?: boolean;

    /**
     * Specifies if the color swatch tooltip is disabled
     */
    isTooltipDisabled?: boolean;

    /**
     * Property that triggers a callback immediately when toggle happens and value of isSelected property should be updated
     */
    onToggle?: (details: ToggleDetail) => void;
  };

/**
 * ColorSwatches render a color, showing focus, hover, and selection variations.
 */
export const ColorSwatch = forwardRef(
  (
    {
      size = 'sm',
      isSelected = false,
      isButton = false,
      onToggle,
      isDisabled = false,
      isTooltipDisabled = false,
      testId,
      value,
      ...props
    }: ColorSwatchProps,
    ref: Ref<SwatchHandle>
  ) => {
    const clientHints = getClientHints();
    const isHybrid = clientHints.hoverSupport === 'events';
    const valueText = value as string;
    const hasValue = valueText && valueText.length;
    const lowerValueText = hasValue ? valueText?.toLocaleLowerCase() : value;
    const isNoFill = hasValue ? lowerValueText === 'transparent' : false;
    const hasAlpha = value && hasValue ? isEightDigitHex(value) : false;
    // const isSmallButton = isButton && size === 'xs'; // if used as xs button, the icon is 20x20, while the target size is 24x24

    const selectedTranslated = translations.vis_stateSelected();
    const noneTranslated = translations.labelNone();
    const colorSwatchTranslated = translations.accColorSwatch();

    const {
      globalEventProps,
      ariaProps: {
        'aria-label': ariaLabel,
        'aria-hidden': ariaHidden,
        'aria-roledescription': ariaRoleDescription,
        ...swatchAriaProps
      }
    } = processGlobals(props);
    const { hoverProps, isHover } = useHover({ isDisabled: !isHybrid });
    const { activeProps, isActive } = useActive({ isDisabled: isDisabled });

    // Cope with rgb or palette colors or illegal colors
    const getHexValue = useCallback(
      (raw?: string) => {
        if (raw?.toLowerCase() === 'transparent') {
          return noneTranslated;
        }
        return parseColorValue(raw);
      },
      [noneTranslated]
    );

    const [accessibleLabel, setAccessibleLabel] = useState<string>(
      ariaLabel
        ? ariaLabel
        : getHexValue(value as string) + isSelected
        ? ' ' + selectedTranslated
        : ''
    );

    const colorScheme = useColorScheme();

    const {
      classes,
      styles: {
        alphaHalfStyles,
        swatchStyles,
        buttonStyles,
        noFillStyles,
        noFillDisabledStyles,
        noFillHoverStyles,
        smallButtonContainerStyles
      }
    } = useComponentTheme<ColorSwatchVariantOptions>(ColorSwatchRedwoodTheme, {
      size: size,
      selected: isSelected ? 'isSelected' : 'notSelected',
      active: isActive ? 'isActive' : 'notActive',
      disabled: isDisabled ? 'isDisabled' : 'notDisabled',
      hover: isHover ? 'isHover' : 'notHover',
      noFill: isNoFill ? 'isNoFill' : 'notNoFill',
      hasValue: hasValue ? 'hasValue' : 'notHasValue',
      button: isButton ? 'isButton' : 'notButton',
      colorScheme
    });

    useEffect(() => {
      if (!ariaLabel) {
        setAccessibleLabel(
          (isSelected ? selectedTranslated + ' ' : '') + getHexValue(value as string)
        );
      }
    }, [isSelected, value, getHexValue, selectedTranslated, ariaLabel]);

    const { triggerProps } = useToggleAction({
      onToggle: onToggle
    });

    const { tooltipContent, tooltipProps } = useTooltip({
      text: accessibleLabel,
      isDisabled: isDisabled || isTooltipDisabled
    });

    const elementDetails = {
      type: 'div' as const
    };

    const splitSwatch = () => {
      return (
        <Fragment>
          <div
            className={alphaHalfStyles}
            style={isDisabled ? {} : { backgroundColor: getSixDigitHex(value!) as string }}
          />
          <div
            className={alphaHalfStyles}
            style={isDisabled ? {} : { backgroundColor: value as string }}
          />
        </Fragment>
      );
    };
    const noFill = () => {
      return (
        <div className={isDisabled ? noFillDisabledStyles : noFillStyles}>
          <NoFill />
        </div>
      );
    };

    const valueSwatch = () => {
      return (
        <div
          className={classNames([
            smallButtonContainerStyles,
            isButton && buttonStyles,
            !isButton && swatchStyles
          ])}
          style={isNoFill || isDisabled || hasAlpha ? {} : { backgroundColor: value as string }}>
          {isNoFill ? noFill() : hasAlpha ? splitSwatch() : undefined}
        </div>
      );
    };

    const noValueSwatch = () => {
      return (
        <div
          className={classNames([
            !isButton && swatchStyles,
            isDisabled ? noFillDisabledStyles : noFillHoverStyles
          ])}>
          <CheckboxCrossed />
        </div>
      );
    };

    const swatchContents = () => {
      return hasValue ? valueSwatch() : noValueSwatch();
    };

    const buttonContents = () => {
      return (
        <IconButton
          aria-label={accessibleLabel}
          aria-roledescription={ariaRoleDescription ?? colorSwatchTranslated}
          {...swatchAriaProps}
          isDisabled={isDisabled}
          ref={ref}
          size="sm"
          testId={testId}
          variant="ghost"
          onAction={() => onToggle?.({ value: isSelected })}>
          {swatchContents()}
        </IconButton>
      );
    };

    return isButton ? (
      buttonContents()
    ) : (
      <Fragment>
        <BareButton
          ref={ref}
          elementDetails={elementDetails}
          {...mergeProps(
            triggerProps,
            globalEventProps,
            hoverProps,
            activeProps,
            globalEventProps,
            tooltipProps
          )}
          testId={testId}
          isDisabled={isDisabled}
          aria-label={accessibleLabel}
          aria-roledescription={ariaRoleDescription ?? colorSwatchTranslated}
          {...swatchAriaProps}
          classNames={classes}
          onAction={() => onToggle?.({ value: isSelected })}>
          {swatchContents()}
        </BareButton>
        {tooltipContent}
      </Fragment>
    );
  }
);

ColorSwatch.displayName = 'ColorSwatch';
